import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { AuthenticationActions } from '@galo-monitor/stores/authentication'
import { FarmUserActions } from '@galo-monitor/stores/farmUser'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import Screen from '@galo-monitor/web-components/Screen'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import UserModal from '@galo-monitor/web-containers/modals/farm/UserModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Users = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: users, mutate, isValidating } = useGaloMonitorApi('/farms/users')

  const userActions = useMemo(
    () => getActionsBySlug(routes.users.slug),
    [getActionsBySlug, routes.users.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('email'),
        field: 'email'
      },
      {
        title: t('phone'),
        field: 'phone'
      },
      {
        title: t('profile', { howMany: 1 }),
        field: 'role'
      },
      {
        title: t('role', { howMany: 1 }),
        field: 'rolePosition'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-user',
        Component: UserModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, user) => {
      createDialog({
        id: 'edit-user',
        Component: UserModal,
        props: {
          user,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onResetPasswordClick = useCallback(
    (_, farm) => {
      const title = t('reset {this}', { this: toLower(t('password')) })
      createDialog({
        id: 'reset-password-farm',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to {action} the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('farm', { howMany: 1 }),
            action: title,
            name: farm.name
          }),
          message: t('a new e-mail will be sent to user'),
          buttonTitle: title,
          buttonProps: {
            color: 'primary'
          },
          onConfirm: () => {
            dispatch(AuthenticationActions.forgotPassword(
              farm.email,
              () => {
                snackbar.success(
                  t('user {name} password reset succesfully', {
                    name: farm.name
                  })
                )
                mutate()
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const onDeleteClick = useCallback(
    (_, user) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('user', { howMany: 1 }),
            name: user.name
          }),
          onConfirm: () => {
            dispatch(FarmUserActions.removeFarmUser(
              user,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('user', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'users-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('user', { howMany: 1 }))
                    })  }.`,
                    message: `${ t('you need detach all farms to delete a user')  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const crumbs = useMemo(() => [
    {
      title: routes.users.title,
      key: 'farm-users-screen',
      url: '/farm/users',
      active: true
    }
  ], [routes.users.title])

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push(
          {
            icon: DataTableIcons.VpnKey,
            tooltip: t('reset {this}', { this: toLower(t('password')) }),
            onClick: onResetPasswordClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small'
            },
            position: 'row'
          }
        )
      }

      return datatableActions
    },
    [isValidating, onResetPasswordClick, t, userActions]
  )

  return (
    <Screen title={ routes.users.title } breadcrumbs={ crumbs }>
      <DataTable
        loading={ isValidating }
        data={ users || [] }
        columns={ columns }
        actions={ actions }
        onAddClick={ userActions.write && onAddClick }
        onEditClick={ userActions.edit && onEditClick }
        onDeleteClick={ userActions.delete && onDeleteClick }
      />
    </Screen>
  )
}

export default Users
